import React from 'react';
import T from 'prop-types';
// components
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router';
import ActivityActionsBar from './ActivityActionsBar/ActivityActionsBar';
import ActivityBox from './ActivityBox';
import ActivityBodyCollapse from './ActivityBodyCollapse';
import ActivityHeader from './ActivityHeader';
import ActivityHeaderBadge from './ActivityHeader/ActivityHeaderBadge';
import ActivityMentions from './ActivityMentions';
import Collapse from 'components/Collapse/Collapse';
import FarmFeedRowComments from 'components/FarmFeed/FarmFeedRowComments';
import FarmFeedRowAddComment from 'components/FarmFeed/FarmFeedRowAddComment';
import FarmTypeLabel from 'components/FarmTypeLabel';
import LastCommentInfo from './LastCommentInfo/LastCommentInfo';
// utils
import classnames from 'classnames/bind';
import moment from 'moment';
// regional date handling
import { connect } from 'react-redux';
import { getCurrentCountryCode } from 'selectors/regionalDateSelectors';
import { formatDateForUser } from 'utils/regionalDateHelper';
// hoc
import withActivityController from './hoc/withActivityController';
// styles
import styles from './GeneralWithdrawalWarning.module.scss';

const cn = classnames.bind(styles);

const affectRerenderKeys = ['id', 'updated_at', 'is_viewed', 'flagged', 'comments', 'events'];

const MortalityTrendWarning = ({
  className,
  activity,
  currentUser,
  isChildActivity,
  markAsSeen,
  openDropdown,
  setActivityFlag,
  isMinimized,
  isTruncated,
  toggleMinimized,
  onViewFullActivity,
  defaultDropdownOptions,
  countryCode,
}) => {
  const { id, events, created_at, flagged, is_viewed, mentions } = activity;
  const isAdmin = currentUser.roles_map.admin;

  const isUserHasRoles = (user, farm) => {
    if (!user.roles_map) return false;
    const { manager, owner, vet, admin, super_admin, tenant_owner } = user.roles_map;
    if (admin || super_admin || tenant_owner) return true;

    const farmId = farm?.id;
    const companyIdOfFarm = farm?.manager?.id || farm?.company_id || null;

    if (farmId) {
      const isManager = manager.farm_ids.includes(farmId) || (
        companyIdOfFarm ? manager.company_ids.includes(companyIdOfFarm) : false
      );
      const isOwner = owner.farm_ids.includes(farmId) || (
        companyIdOfFarm ? owner.company_ids.includes(companyIdOfFarm) : false
      );
      const isVet = vet.farm_ids.includes(farmId) || (
        companyIdOfFarm ? vet.company_ids.includes(companyIdOfFarm) : false
      );
      return isManager || isOwner || isVet; // caregivers intentionally excluded
    }

    const isManager = !!manager.farm_ids.length || !!manager.company_ids.length;
    const isOwner = !!owner.farm_ids.length || !!owner.company_ids.length;
    const isVet = !!vet.farm_ids.length || !!vet.company_ids.length;
    return isManager || isOwner || isVet;
  };

  const farmIds = new Set(events.map(({ farm_id }) => farm_id));
  const farmsData = [...farmIds].map((farmId) => {
    const findFarm = ({ farm_id }) => farm_id === farmId;
    const { farm } = events.find(findFarm);
    const farmEvents = events.filter(findFarm);
    return {
      farm,
      data: farmEvents
        .filter(({ additional_data }) => !!additional_data)
        .map(({ additional_data, pig_group }) => ({ additional_data, pig_group })),
    };
  }).filter(({ farm, data }) => isUserHasRoles(currentUser, farm) && data.length > 0);

  const activeDays = (() => {
    const firstEvent = events[0];
    return firstEvent?.additional_data?.period_days || 7;
  })();

  const renderValue = (value, isExceeded = false) => (
    <span className={cn('cell')}>
      <span className={cn('value', { exceeded: isExceeded })}>{value}</span>
    </span>
  );

  const renderGroupName = (pig_group) => {
    if (isAdmin && pig_group?.id) {
      return (
        <Link to={`/barnsheets/groups/${pig_group.id}`} className="color-primary">
          {pig_group.name}
        </Link>
      );
    }
    return pig_group?.name || 'N/A';
  };

  return (
    <ActivityBox
      className={className}
      isFlagged={flagged}
      isTruncated={isTruncated}
      hasMarker={!isChildActivity}
      isUnseen={!is_viewed}
      markAsSeen={markAsSeen}
      onViewFullActivity={onViewFullActivity}
    >
      {!isMinimized &&
        <LastCommentInfo activity={activity} currentUser={currentUser} />
      }
      <ActivityHeader
        activityId={id}
        onOpenDropdown={(e) => openDropdown(e, defaultDropdownOptions)}
        onDoubleClick={toggleMinimized}
      >
        <ActivityHeaderBadge isCentered>
          <FormattedMessage id="general.mortalityWarning" tagName="b" />
        </ActivityHeaderBadge>
      </ActivityHeader>
      <ActivityBodyCollapse isClosed={isMinimized}>
        <div className={cn('warning-text')}>
          <FormattedMessage
            tagName="div"
            id="component.generalMortalityTrendWarning.text"
            values={{
              b: (msg) => <b>{msg}</b>,
              orange: (msg) => <span className={cn('brand-orange')}>{msg}</span>,
              days: activeDays,
              date: (<b>{formatDateForUser(moment(activity.created_at).subtract(1, 'day'), countryCode)}</b>),
            }}
          />
          <div className={cn('timestamp')}>
            {moment(created_at).format('MMM DD, YYYY, hh:mm a')}
          </div>
          <ActivityMentions isAdmin={isAdmin} users={mentions} />
        </div>
        {!!farmsData.length && (
          <div className="ph-30 mb-25">
            <Collapse
              className={cn('collapse')}
              title={<FormattedMessage id="general.button.showDetails" />}
            >
              {farmsData.map(({ farm, data }, idx) => (
                <Collapse
                  key={idx}
                  className={cn('collapse', 'child')}
                  title={<span>{farm?.name}<FarmTypeLabel className="ml-5" farmType={farm?.farm_type} /></span>}
                >
                  <div className={cn('collapse-body')}>
                    <div className={cn('row', 'header', 'mortality-trend')}>
                      <div className={cn('column-header')}>
                        <FormattedMessage id="component.mortalityTrend.table.groupId" />
                      </div>
                      <div className={cn('column-header')}>
                        <FormattedMessage id="component.mortalityTrend.table.total" />
                      </div>
                      <div className={cn('column-header')}>
                        <FormattedMessage id="component.mortalityTrend.table.acute" />
                      </div>
                      <div className={cn('column-header')}>
                        <FormattedMessage id="component.mortalityTrend.table.chronic" />
                      </div>
                      <div className={cn('column-header')}>
                        <FormattedMessage id="component.mortalityTrend.table.euthanasia" />
                      </div>
                      <div className={cn('column-header')}>
                        <FormattedMessage id="component.mortalityTrend.table.treated" />
                      </div>
                      <div className={cn('column-header')}>
                        <FormattedMessage
                          id="component.mortalityTrend.table.percent"
                          values={{ days: activeDays }}
                        />
                      </div>
                    </div>
                    {data.map(({ additional_data, pig_group }, pgIdx) => (
                      <div className={cn('row', 'mortality-trend')} key={pgIdx}>
                        <div className={cn('group-name')}>
                          {renderGroupName(pig_group)}
                        </div>
                        <div>
                          {renderValue(
                            additional_data.total_mortalities,
                            additional_data.exceeded?.hard
                          )}
                        </div>
                        <div>
                          {renderValue(
                            additional_data.deaths?.acute || 0,
                            additional_data.exceeded?.causes?.acute
                          )}
                        </div>
                        <div>
                          {renderValue(
                            additional_data.deaths?.chronic || 0,
                            additional_data.exceeded?.causes?.chronic
                          )}
                        </div>
                        <div>
                          {renderValue(
                            additional_data.deaths?.euthanasia || 0,
                            additional_data.exceeded?.causes?.euthanasia
                          )}
                        </div>
                        <div>
                          {renderValue(
                            additional_data.deaths?.treated || 0,
                            additional_data.exceeded?.causes?.treated
                          )}
                        </div>
                        <div style={{ fontWeight: 'bold' }}>
                          {renderValue(
                            `${(additional_data.percent || 0).toFixed(2)}%`,
                            additional_data.exceeded?.percent
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </Collapse>
              ))}
            </Collapse>
          </div>
        )}
        {!isChildActivity && (
          <>
            <ActivityActionsBar
              activity={activity}
              currentUser={currentUser}
              openDropdown={openDropdown}
              setActivityFlag={setActivityFlag}
            />
            <FarmFeedRowComments activity={activity} />
            <FarmFeedRowAddComment activity={activity} />
          </>
        )}
      </ActivityBodyCollapse>
    </ActivityBox>
  );
};

MortalityTrendWarning.contextTypes = {
  router: T.object.isRequired
};

MortalityTrendWarning.propTypes = {
  className: T.string,
  activity: T.object.isRequired,
  currentUser: T.object.isRequired,
  markAsSeen: T.func,
  isChildActivity: T.bool,
  openDropdown: T.func.isRequired,
  setActivityFlag: T.func.isRequired,
  // from hoc
  isTruncated: T.bool.isRequired,
  isMinimized: T.bool.isRequired,
  toggleMinimized: T.func.isRequired,
  onViewFullActivity: T.func.isRequired,
  defaultDropdownOptions: T.array.isRequired,
  countryCode: T.string,
};

MortalityTrendWarning.defaultProps = {
  isChildActivity: false,
};

const ConnectedComponent = connect(
  (state) => ({
    countryCode: getCurrentCountryCode(state),
  })
)(MortalityTrendWarning);

export default withActivityController(ConnectedComponent, { affectRerenderKeys });
